import { PlusOutlined, DeleteOutlined, CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import {
	Button, Card, Checkbox, Divider, Form, Input, Select, Tag, Typography, Space, Alert,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from '../config/dayjs';
import axios from '../config/axios';
import { handleAPIError } from '../utils/helpers';

const { Title, Text } = Typography;

interface NOK {
	id?: number;
	name: string;
	relationship: string;
	source_of_income: string;
	location: string;
	azima_status: string;
	is_rejected: boolean;
}

interface VerificationCallData {
	id: number;
	source_of_income: string;
	location: string;
	how_they_know_zazu: string;
	azima_status: string;
	contacts_confirmed: boolean;
	contacts_count: number | null;
	phone_condition: string;
	outcome: string;
	notes: string;
	created_by: string | null;
	created_at: string;
	updated_at: string;
	noks: NOK[];
}

interface FormValues {
	source_of_income: string;
	location: string;
	how_they_know_zazu: string;
	azima_status: string;
	contacts_confirmed: boolean;
	contacts_count: number | null;
	phone_condition: string;
	outcome: string;
	notes: string;
	noks: NOK[];
}

const AZIMA_STATUS_OPTIONS = [
	{ value: 'no_account', label: 'No Account' },
	{ value: 'good', label: 'Good' },
	{ value: 'defaulted', label: 'Defaulted' },
];

const PHONE_CONDITION_OPTIONS = [
	{ value: 'good', label: 'Good' },
	{ value: 'fair', label: 'Fair' },
	{ value: 'poor', label: 'Poor' },
];

const OUTCOME_OPTIONS = [
	{ value: 'verified', label: 'Verified' },
	{ value: 'failed', label: 'Failed' },
	{ value: 'unreachable', label: 'Unreachable' },
	{ value: 'pending', label: 'Pending' },
];

const outcomeColor: Record<string, string> = {
	verified: 'green',
	failed: 'red',
	unreachable: 'orange',
	pending: 'blue',
};

const azimaColor: Record<string, string> = {
	no_account: 'default',
	good: 'green',
	defaulted: 'red',
};

function azimaLabel(v: string) {
	return AZIMA_STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

interface Props {
	userId: number;
	readOnly?: boolean;
}

export const UserVerificationCall = ({ userId, readOnly = false }: Props) => {
	const queryClient = useQueryClient();
	const [form] = Form.useForm<FormValues>();

	const { data: callData, isLoading } = useQuery<VerificationCallData | null>({
		queryKey: ['verification-call', userId],
		queryFn: async () => {
			const res = await axios.get(`/admin/users/${userId}/verification-call`, { withCredentials: true });
			return res.data.results ?? null;
		},
		enabled: !!userId,
	});

	const isExisting = !!callData;

	const save = useMutation({
		mutationFn: async (values: FormValues) => {
			const method = isExisting ? 'patch' : 'post';
			const res = await axios[method](`/admin/users/${userId}/verification-call`, values, { withCredentials: true });
			return res.data.results as VerificationCallData;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['verification-call', userId] });
		},
		onError: handleAPIError,
	});

	if (isLoading) {
		return <div className="py-8 text-center text-gray-400">Loading...</div>;
	}

	const initialValues: Partial<FormValues> = callData
		? {
			source_of_income: callData.source_of_income,
			location: callData.location,
			how_they_know_zazu: callData.how_they_know_zazu,
			azima_status: callData.azima_status || undefined,
			contacts_confirmed: callData.contacts_confirmed,
			contacts_count: callData.contacts_count,
			phone_condition: callData.phone_condition || undefined,
			outcome: callData.outcome,
			notes: callData.notes,
			noks: callData.noks,
		}
		: { noks: [], contacts_confirmed: false, outcome: 'pending' };

	return (
		<div className="space-y-4">
			{isExisting && (
				<Alert
					type="success"
					icon={<CheckCircleOutlined />}
					showIcon
					message={
						<span>
							Verification call recorded by <strong>{callData.created_by ?? 'Unknown'}</strong> on{' '}
							{dayjs(callData.created_at).format('DD MMM YYYY, h:mm A')}
							{callData.updated_at !== callData.created_at && (
								<span className="ml-2 text-xs text-gray-500">
									(last updated {dayjs(callData.updated_at).format('DD MMM YYYY, h:mm A')})
								</span>
							)}
						</span>
					}
				/>
			)}

			{readOnly && (
				<Alert
					type="warning"
					icon={<LockOutlined />}
					showIcon
					message="This user is verified — the verification call record is locked. Only Super Admins can edit it."
				/>
			)}

			<Form
				form={form}
				layout="vertical"
				initialValues={initialValues}
				onFinish={(values) => save.mutate(values)}
				size="small"
			>
				{/* Customer section */}
				<Card title="Customer" size="small" className="mb-4">
					<div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
						<Form.Item name="source_of_income" label="Source of Income" rules={[{ required: !readOnly }]}>
							<Input.TextArea rows={2} placeholder="e.g. Welder at Mistone Metal Works, Mbarara" disabled={readOnly} />
						</Form.Item>
						<Form.Item name="location" label="Stays In" rules={[{ required: !readOnly }]}>
							<Input.TextArea rows={2} placeholder="e.g. Mbarara along Ntare Road" disabled={readOnly} />
						</Form.Item>
						<Form.Item name="how_they_know_zazu" label="How They Know Zazu">
							<Input placeholder="e.g. TikTok, Friend, Radio" disabled={readOnly} />
						</Form.Item>
						<Form.Item name="azima_status" label="Azima Status" rules={[{ required: !readOnly }]}>
							<Select options={AZIMA_STATUS_OPTIONS} placeholder="Select status" disabled={readOnly} />
						</Form.Item>
						<Form.Item name="phone_condition" label="Phone Condition" rules={[{ required: !readOnly }]}>
							<Select options={PHONE_CONDITION_OPTIONS} placeholder="Select condition" disabled={readOnly} />
						</Form.Item>
						<div>
							<Form.Item name="contacts_confirmed" valuePropName="checked" label=" ">
								<Checkbox disabled={readOnly}>Contacts confirmed</Checkbox>
							</Form.Item>
							<Form.Item
								noStyle
								shouldUpdate={(prev, cur) => prev.contacts_confirmed !== cur.contacts_confirmed}
							>
								{({ getFieldValue }) =>
									getFieldValue('contacts_confirmed') && (
										<Form.Item name="contacts_count" label="Contact count">
											<Input type="number" min={0} placeholder="e.g. 242" style={{ width: 120 }} disabled={readOnly} />
										</Form.Item>
									)
								}
							</Form.Item>
						</div>
					</div>
				</Card>

				{/* NOK section */}
				<Form.List name="noks">
					{(fields, { add, remove }) => (
						<div className="space-y-3">
							{fields.map((field, index) => (
								<Card
									key={field.key}
									size="small"
									title={
										<Space>
											<span>Next of Kin {index + 1}</span>
											<Form.Item
												name={[field.name, 'is_rejected']}
												valuePropName="checked"
												noStyle
											>
												<Checkbox disabled={readOnly}>Rejected</Checkbox>
											</Form.Item>
										</Space>
									}
									extra={
										!readOnly && (
											<Button
												type="text"
												danger
												size="small"
												icon={<DeleteOutlined />}
												onClick={() => remove(field.name)}
											/>
										)
									}
								>
									<div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
										<Form.Item name={[field.name, 'name']} label="Name" rules={[{ required: !readOnly }]}>
											<Input placeholder="Full name" disabled={readOnly} />
										</Form.Item>
										<Form.Item name={[field.name, 'relationship']} label="Relationship" rules={[{ required: !readOnly }]}>
											<Input placeholder="e.g. Wife, Brother" disabled={readOnly} />
										</Form.Item>
										<Form.Item name={[field.name, 'source_of_income']} label="Source of Income">
											<Input.TextArea rows={2} placeholder="e.g. Saloon owner" disabled={readOnly} />
										</Form.Item>
										<Form.Item name={[field.name, 'location']} label="Stays In">
											<Input.TextArea rows={2} placeholder="e.g. Sheema, Kyangyeyi" disabled={readOnly} />
										</Form.Item>
										<Form.Item name={[field.name, 'azima_status']} label="Azima Status" rules={[{ required: !readOnly }]}>
											<Select options={AZIMA_STATUS_OPTIONS} placeholder="Select status" disabled={readOnly} />
										</Form.Item>
									</div>
								</Card>
							))}

							{!readOnly && (
								<Button
									type="dashed"
									onClick={() => add({ name: '', relationship: '', source_of_income: '', location: '', azima_status: '', is_rejected: false })}
									icon={<PlusOutlined />}
									block
								>
									Add Next of Kin
								</Button>
							)}
						</div>
					)}
				</Form.List>

				<Divider />

				{/* Outcome & notes */}
				<Card title="Call Outcome" size="small" className="mb-4">
					<div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
						<Form.Item name="outcome" label="Outcome" rules={[{ required: !readOnly }]}>
							<Select options={OUTCOME_OPTIONS} disabled={readOnly} />
						</Form.Item>
						<Form.Item name="notes" label="Additional Notes">
							<Input.TextArea rows={2} placeholder="Anything else worth noting..." disabled={readOnly} />
						</Form.Item>
					</div>
				</Card>

				{!readOnly && (
					<div className="flex justify-end">
						<Button type="primary" htmlType="submit" loading={save.isPending}>
							{isExisting ? 'Update Verification Call' : 'Save Verification Call'}
						</Button>
					</div>
				)}
			</Form>

			{/* Read-only summary when saved — quick glance below the form */}
			{isExisting && (
				<>
					<Divider>Saved Summary</Divider>
					<div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
						<div>
							<Text type="secondary">Outcome</Text>
							<div><Tag color={outcomeColor[callData.outcome]}>{callData.outcome.toUpperCase()}</Tag></div>
						</div>
						<div>
							<Text type="secondary">Phone Condition</Text>
							<div><Tag>{callData.phone_condition || '—'}</Tag></div>
						</div>
						<div>
							<Text type="secondary">Customer Azima</Text>
							<div><Tag color={azimaColor[callData.azima_status]}>{azimaLabel(callData.azima_status)}</Tag></div>
						</div>
						<div>
							<Text type="secondary">Contacts</Text>
							<div>{callData.contacts_confirmed ? `Yes (${callData.contacts_count ?? '?'})` : 'Not confirmed'}</div>
						</div>
					</div>

					{callData.noks.length > 0 && (
						<div className="mt-3 space-y-2">
							<Title level={5} style={{ marginBottom: 4 }}>Next of Kin</Title>
							{callData.noks.map((nok, i) => (
								<div key={i} className="flex flex-wrap items-center gap-2 rounded border border-gray-100 bg-gray-50 p-2 text-sm">
									<strong>{nok.name}</strong>
									<Text type="secondary">({nok.relationship})</Text>
									<Tag color={azimaColor[nok.azima_status]}>{azimaLabel(nok.azima_status)}</Tag>
									{nok.is_rejected && <Tag color="red">Rejected</Tag>}
								</div>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
};
